/**
 * AttendanceTimeBand.tsx
 * 출석부 - 시간띠(연속 밴드) 뷰
 *
 * 설계: 시간이 단일 진실. 각 학생 하루를 연속 색 세그먼트로 표시.
 *  - 하늘색: 착석 중(present)   빨강: 필수인데 없음(absent)
 *  - 회색: 예정(future)         노랑: 승인 외출(outing)   빗금: 사유서 면제(exempt)
 *  - 교시(periods)는 표시용 구분선/라벨 (판정 아님)
 * 백엔드: GET /v1/owner/attendance-period-grid/time-band
 */

import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../../services/authApi';

interface Segment {
  start: string;      // "HH:MM"
  end: string;        // "HH:MM"
  state: string;      // present | absent | future | outing | exempt
  start_min: number;
  end_min: number;
}

interface PeriodStatus {
  period_number?: number | null;
  period_name?: string | null;
  status: string;   // present | absent | outing | exempt | future | none
}

interface BandStudent {
  student_id: string;
  name: string;
  seat_number?: number | null;
  grade?: string | null;
  class_name?: string | null;
  segments: Segment[];
  period_statuses?: PeriodStatus[];
  status: string;
  absence_minutes?: number | null;
  is_exempt: boolean;
  has_schedule: boolean;
}

interface BandPeriod {
  period_number?: number | null;
  period_name?: string | null;
  period_type?: string | null;
  start_time?: string;
  end_time?: string;
  start_min?: number | null;
  end_min?: number | null;
}

interface BandResponse {
  room_id: string;
  room_name: string;
  date: string;
  now: string;
  now_min: number | null;
  day_start_min: number;
  day_end_min: number;
  periods: BandPeriod[];
  students: BandStudent[];
  summary?: { total_students: number; scheduled_students: number };
}

interface AttendanceTimeBandProps {
  roomId?: string | null;
  targetDate?: string;   // YYYY-MM-DD
  onStudentClick?: (studentId: string) => void;
}

// 세그먼트 상태 → 표시 스타일
export const SEG_STYLE: Record<string, { className: string; style?: React.CSSProperties; label: string }> = {
  present: { className: 'bg-sky-400', label: '착석' },
  absent: { className: 'bg-red-500', label: '미출석' },
  // '예정'만 쓰면 점주가 쉬는시간으로 읽는다(실제 제보). 뜻을 그대로 적는다.
  future: { className: 'bg-blue-200', label: '아직 안 온 시간' },
  outing: { className: 'bg-yellow-300', label: '외출' },
  exempt: {
    className: '',
    // 빗금(사유서 면제) — repeating-linear-gradient
    style: {
      backgroundImage:
        'repeating-linear-gradient(45deg, #c4b5fd 0, #c4b5fd 4px, #ede9fe 4px, #ede9fe 8px)',
    },
    label: '면제',
  },
};

// 교시별출결 마크 — 밴드 위에 교시 단위로 겹쳐 표시 (흰 칩 위 색기호 → 어떤 세그먼트 색 위에도 잘 보임)
export const PERIOD_MARK: Record<string, { label: string; className: string }> = {
  present: { label: '○', className: 'text-sky-600' },
  absent: { label: '×', className: 'text-red-600' },
  outing: { label: '△', className: 'text-yellow-600' },
  exempt: { label: '□', className: 'text-purple-600' },
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  present: { label: '입실중', className: 'bg-sky-100 text-sky-800' },
  absent: { label: '미출석', className: 'bg-red-100 text-red-800' },
  out: { label: '외출', className: 'bg-yellow-100 text-yellow-800' },
  exempt: { label: '출석면제', className: 'bg-purple-100 text-purple-800' },
  exited: { label: '퇴실', className: 'bg-gray-100 text-gray-600' },
  scheduled: { label: '입실예정', className: 'bg-gray-100 text-gray-600' },
  none: { label: '스케줄없음', className: 'bg-gray-100 text-gray-500' },
};

const minToHHMM = (m: number): string => {
  const mm = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(mm / 60);
  const mi = mm % 60;
  return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
};

export const AttendanceTimeBand: React.FC<AttendanceTimeBandProps> = ({
  roomId,
  targetDate,
  onStudentClick,
}) => {
  const [data, setData] = useState<BandResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!roomId) {
      setData(null);
      return;
    }
    let cancelled = false;
    const fetchBand = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = { room_id: roomId };
        if (targetDate) params.target_date = targetDate;
        const res = await apiClient.get('/v1/owner/attendance-period-grid/time-band', { params });
        if (!cancelled) setData(res.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.message || '시간띠 데이터 조회 실패');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBand();
    return () => {
      cancelled = true;
    };
  }, [roomId, targetDate]);

  const span = data ? Math.max(1, data.day_end_min - data.day_start_min) : 1;
  const pct = (m: number): number => {
    if (!data) return 0;
    return ((m - data.day_start_min) / span) * 100;
  };

  // 눈금(정시) 라벨 — 밴드 상단 시간축
  const hourTicks = useMemo(() => {
    if (!data) return [] as number[];
    const ticks: number[] = [];
    const startH = Math.ceil(data.day_start_min / 60);
    const endH = Math.floor(data.day_end_min / 60);
    for (let h = startH; h <= endH; h++) ticks.push(h * 60);
    return ticks;
  }, [data]);

  const filteredStudents = useMemo(() => {
    if (!data) return [] as BandStudent[];
    let rows = data.students;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (s) =>
          s.name?.toLowerCase().includes(q) ||
          String(s.seat_number ?? '').includes(q),
      );
    }
    // 좌석번호 순
    return [...rows].sort(
      (a, b) => (Number(a.seat_number) || 0) - (Number(b.seat_number) || 0),
    );
  }, [data, search]);

  if (!roomId) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
        룸을 선택하세요.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* 상단 바 — 검색 + 범례 */}
      <div className="p-4 border-b border-gray-200 flex items-center gap-4 flex-wrap">
        <input
          type="text"
          placeholder="학생명 또는 좌석번호 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm max-w-xs"
        />
        <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
          {(['present', 'absent', 'future', 'outing', 'exempt'] as const).map((k) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span
                className={`inline-block w-4 h-3 rounded-sm ${SEG_STYLE[k].className}`}
                style={SEG_STYLE[k].style}
              />
              {SEG_STYLE[k].label}
            </span>
          ))}
        </div>
        {data && (
          <div className="ml-auto text-sm text-gray-500">
            총 {filteredStudents.length}명 · 스케줄 {data.summary?.scheduled_students ?? 0}명
            <span className="ml-2 text-blue-600">현재 {data.now}</span>
          </div>
        )}
      </div>

      {loading && <div className="p-8 text-center text-gray-400">불러오는 중...</div>}
      {error && <div className="p-8 text-center text-red-500">⚠️ {error}</div>}

      {data && !loading && (
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            {/* 시간축 헤더 (교시 구분선 + 정시 눈금) */}
            <div className="flex items-end border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
              <div className="w-40 shrink-0 px-3 py-2 text-xs font-medium text-gray-500">
                학생 (좌석)
              </div>
              <div className="relative flex-1 h-9 mr-3">
                {/* 정시 눈금 */}
                {hourTicks.map((m) => (
                  <div
                    key={`tick-${m}`}
                    className="absolute top-0 bottom-0 border-l border-gray-100"
                    style={{ left: `${pct(m)}%` }}
                  >
                    <span className="absolute top-0 left-0.5 text-[10px] text-gray-400">
                      {minToHHMM(m).slice(0, 2)}
                    </span>
                  </div>
                ))}
                {/* 교시 라벨 */}
                {data.periods.map((p, i) =>
                  p.start_min != null && p.end_min != null ? (
                    <span
                      key={`pl-${i}`}
                      className="absolute bottom-0 -translate-x-1/2 text-[10px] text-gray-500 whitespace-nowrap"
                      style={{ left: `${(pct(p.start_min) + pct(p.end_min)) / 2}%` }}
                    >
                      {p.period_name}
                    </span>
                  ) : null,
                )}
                {/* 현재 시각 마커 */}
                {data.now_min != null &&
                  data.now_min >= data.day_start_min &&
                  data.now_min <= data.day_end_min && (
                    <div
                      className="absolute top-0 bottom-0 border-l-2 border-red-500 z-20"
                      style={{ left: `${pct(data.now_min)}%` }}
                    />
                  )}
              </div>
            </div>

            {/* 학생별 밴드 행 */}
            <div className="divide-y divide-gray-100">
              {filteredStudents.map((s) => {
                const badge = STATUS_BADGE[s.status] || STATUS_BADGE.none;
                return (
                  <div
                    key={s.student_id}
                    className={`flex items-center hover:bg-gray-50 ${
                      onStudentClick ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => onStudentClick?.(s.student_id)}
                  >
                    <div className="w-40 shrink-0 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {s.name}
                        </span>
                        {s.seat_number != null && (
                          <span className="text-xs text-gray-400">#{s.seat_number}</span>
                        )}
                      </div>
                      <span
                        className={`inline-flex mt-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded ${badge.className}`}
                      >
                        {badge.label}
                        {s.status === 'absent' && s.absence_minutes
                          ? ` ${s.absence_minutes}분`
                          : ''}
                      </span>
                    </div>
                    <div className="relative flex-1 h-8 my-1.5 mr-3 rounded-md border border-gray-200 bg-slate-100 overflow-hidden">
                      {/* 세그먼트 (전체 높이) */}
                      {s.segments.map((seg, i) => {
                        const st = SEG_STYLE[seg.state] || SEG_STYLE.future;
                        const left = pct(seg.start_min);
                        const width = pct(seg.end_min) - left;
                        return (
                          <div
                            key={`seg-${i}`}
                            className={`absolute inset-y-0 ${st.className}`}
                            style={{ left: `${left}%`, width: `${width}%`, ...st.style }}
                            title={`${seg.start}~${seg.end} ${st.label}`}
                          />
                        );
                      })}
                      {/* 교시 구분선 (얇은 흰선) */}
                      {data.periods.map((p, i) =>
                        p.start_min != null && p.start_min > data.day_start_min ? (
                          <div
                            key={`div-${i}`}
                            className="absolute inset-y-0 w-px bg-white/70 z-[3]"
                            style={{ left: `${pct(p.start_min)}%` }}
                          />
                        ) : null,
                      )}
                      {/* 교시별출결 마크 (교시 중앙에 흰 칩) */}
                      {s.period_statuses?.map((ps, i) => {
                        const mark = PERIOD_MARK[ps.status];
                        if (!mark) return null;
                        const p = data.periods.find(
                          (pp) => pp.period_number === ps.period_number,
                        );
                        if (!p || p.start_min == null || p.end_min == null) return null;
                        const center = (pct(p.start_min) + pct(p.end_min)) / 2;
                        return (
                          <span
                            key={`ps-${i}`}
                            className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-[10px] font-bold leading-none ring-1 ring-black/5 shadow-sm ${mark.className}`}
                            style={{ left: `${center}%` }}
                            title={`${p.period_name ?? ''} ${mark.label}`}
                          >
                            {mark.label}
                          </span>
                        );
                      })}
                      {/* 현재 시각 마커 */}
                      {data.now_min != null &&
                        data.now_min >= data.day_start_min &&
                        data.now_min <= data.day_end_min && (
                          <div
                            className="absolute inset-y-0 w-0.5 bg-red-500 z-10"
                            style={{ left: `${pct(data.now_min)}%` }}
                          />
                        )}
                    </div>
                  </div>
                );
              })}

              {filteredStudents.length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500">
                  {search ? '검색 조건에 맞는 학생이 없습니다.' : '학생이 없습니다.'}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
